import { ApiResponse, PaginatedResponse } from "../types/common";
import {
  Student,
  StudentWithUserInfo,
  CreateStudentRequest,
  UpdateStudentRequest,
  StudentFilter,
  WhatsappGroup,
} from "../types/student";
import api from "./api";

// Student CRUD Operations
export const getStudents = async (
  filters: StudentFilter = {}
): Promise<PaginatedResponse<StudentWithUserInfo>> => {
  try {
    const params = new URLSearchParams();

    if (filters.page) params.append("page", filters.page.toString());
    if (filters.limit) params.append("limit", filters.limit.toString());
    if (filters.search) params.append("search", filters.search);
    if (filters.classId) params.append("classId", filters.classId);
    if (filters.courseId) params.append("courseId", filters.courseId);
    if (filters.departmentId)
      params.append("departmentId", filters.departmentId);
    if (filters.status) params.append("status", filters.status);
    if (filters.isActive !== undefined)
      params.append("isActive", filters.isActive.toString());
    if (filters.gender) params.append("gender", filters.gender);
    if (filters.enrollmentStatus)
      params.append("enrollmentStatus", filters.enrollmentStatus);
    if (filters.includeEcourse)
      params.append("includeEcourse", filters.includeEcourse);

    const response = await api.get(`/students?${params.toString()}`);

    // Return the response directly as the backend already returns the correct structure
    // Note: axios interceptor already returns response.data, so response here is the actual data
    const data = response as any;
    return {
      success: data.success,
      data: data.students || [],
      pagination: {
        currentPage: data.currentPage || 1,
        totalPages: data.totalPages || 1,
        totalItems: data.total || 0,
        hasNext: (data.currentPage || 1) < (data.totalPages || 1),
        hasPrev: (data.currentPage || 1) > 1,
      },
    };
  } catch (error) {
    console.error("Error fetching students:", error);
    // Return a default structure for error cases
    return {
      success: false,
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        hasNext: false,
        hasPrev: false,
      },
    };
  }
};

export const getStudentById = async (
  id: string
): Promise<ApiResponse<Student>> => {
  try {
    const response = await api.get(`/students/${id}`);
    const data = response as any;
    return {
      success: data.success,
      data: data.student,
      message: data.message,
    };
  } catch (error) {
    console.error("Error fetching student by ID:", error);
    return {
      success: false,
      data: {} as Student,
      message: "Failed to fetch student",
    };
  }
};

export const createStudent = async (
  data: CreateStudentRequest
): Promise<ApiResponse<Student>> => {
  try {
    const response = await api.post("/students", data);
    return response as unknown as any;
  } catch (error: any) {
    console.error("Error creating student:", error);
    throw error; // Throw to let component handle
  }
};

export const updateStudent = async (
  id: string,
  data: UpdateStudentRequest
): Promise<ApiResponse<Student>> => {
  try {
    const response = await api.put(`/students/${id}`, data);
    return response as unknown as any;
  } catch (error: any) {
    console.error("Error updating student:", error);
    throw error; // Throw to let component handle
  }
};

export const deleteStudent = async (id: string): Promise<ApiResponse<void>> => {
  try {
    const response = await api.delete(`/students/${id}`);
    return response as unknown as any;
  } catch (error) {
    console.error("Error deleting student:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to delete student",
    };
  }
};

export const updateStudentStatus = async (
  id: string,
  academicStatus: string,
  reason?: string
): Promise<ApiResponse<any>> => {
  try {
    const response = await api.put(`/students/${id}/status`, {
      academicStatus,
      reason,
    });
    return response as unknown as any;
  } catch (error) {
    console.error("Error updating student status:", error);
    return {
      success: false,
      data: {},
      message: "Failed to update student status",
    };
  }
};

// Student Statistics
export const getStudentStats = async (
  branchId?: string,
  filters?: { startDate?: string; endDate?: string }
): Promise<ApiResponse<any>> => {
  try {
    const params = new URLSearchParams();
    if (branchId) params.append("branchId", branchId);
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);

    const queryString = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get(`/students/statistics${queryString}`);

    // Response is already the data (due to axios interceptor), not response.data
    // Transform API response structure from { success, statistics } to { success, data }
    if (response && (response as any).statistics) {
      const transformedData = {
        success: (response as any).success,
        data: (response as any).statistics,
      };
      return transformedData;
    }

    return response as unknown as ApiResponse<any>;
  } catch (error) {
    console.error("Error fetching student statistics:", error);
    // Return fallback data
    const fallbackData = {
      success: true,
      data: {
        totalStudents: 0,
        activeStudents: 0,
        inactiveStudents: 0,
        newEnrollments: 0,
        averageAge: 0,
        studentsByGender: {},
        studentsByStatus: {},
      },
    };
    return fallbackData;
  }
};

// Bulk Operations
export const bulkUpdateStudents = async (
  ids: string[],
  data: Partial<UpdateStudentRequest>
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.put("/students/bulk", { ids, data });
    return response as unknown as any;
  } catch (error) {
    console.error("Error bulk updating students:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to bulk update students",
    };
  }
};

export const bulkDeleteStudents = async (
  ids: string[]
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.delete("/students/bulk", { data: { ids } });
    return response as unknown as any;
  } catch (error) {
    console.error("Error bulk deleting students:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to bulk delete students",
    };
  }
};

// Additional Student Operations
export const enrollStudentInClass = async (
  studentId: string,
  classId: string
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post(`/students/${studentId}/enroll`, {
      classId,
    });
    return response as unknown as any;
  } catch (error) {
    console.error("Error enrolling student in class:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to enroll student in class",
    };
  }
};

export const unenrollStudentFromClass = async (
  studentId: string,
  classId: string
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post(`/students/${studentId}/unenroll`, {
      classId,
    });
    return response as unknown as any;
  } catch (error) {
    console.error("Error unenrolling student from class:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to unenroll student from class",
    };
  }
};

export const getStudentsByClass = async (
  classId: string
): Promise<ApiResponse<Student[]>> => {
  try {
    const response = await api.get(`/classes/${classId}/students`);
    return response as unknown as any;
  } catch (error) {
    console.error("Error fetching students by class:", error);
    return {
      success: false,
      data: [],
      message: "Failed to fetch students by class",
    };
  }
};

// Get student payment history
export const getStudentPaymentHistory = async (
  studentId: string
): Promise<
  ApiResponse<{
    student: {
      _id: string;
      studentId: string;
      userInfo: {
        firstName: string;
        lastName: string;
        email: string;
        profileDetails?: any;
      };
      class?: {
        name: string;
        capacity: number;
      };
      branch: {
        name: string;
        configuration?: any;
      };
      enrollmentDate: string;
    };
    feeSummary: {
      totalFeeStructure: number;
      totalPaid: number;
      totalBalance: number;
      feeStatus: string;
      scholarshipAmount: number;
      lastPaymentDate: string | null;
      totalPayments: number;
    };
    paymentHistory: Array<{
      amount: number;
      paymentMethod: string;
      referenceNumber?: string;
      paymentDate: string;
      notes?: string;
    }>;
  }>
> => {
  try {
    const response = await api.get(`/students/${studentId}/payments`);
    return response as any;
  } catch (error) {
    console.error("Error fetching student payment history:", error);
    return {
      success: false,
      data: null as any,
      message: "Failed to fetch payment history",
    };
  }
};

// Legacy functions for backward compatibility
export const fetchStudents = async (): Promise<Student[]> => {
  const response = await getStudents();
  return response as unknown as any;
};

export const getMyStudentProfile = async (): Promise<
  ApiResponse<StudentWithUserInfo>
> => {
  try {
    const response = await api.get(`/students/me`);
    // Backend should return { success: true, student: {...} }
    return {
      success: (response as any).success,
      data: (response as any).student,
      message: (response as any).message,
    };
  } catch (error) {
    console.error("Error fetching my student profile:", error);
    return {
      success: false,
      data: {} as StudentWithUserInfo,
      message: "Failed to fetch student profile",
    };
  }
};

export const getStudentWhatsappGroups = async (): Promise<
  ApiResponse<WhatsappGroup[]>
> => {
  try {
    const response = await api.get(`/students/whatsapp-groups`);
    return {
      success: (response as any).success,
      data: (response as any).data || [],
      message: (response as any).message,
    };
  } catch (error) {
    console.error("Error fetching WhatsApp groups:", error);
    return {
      success: false,
      data: [],
      message: "Failed to fetch WhatsApp groups",
    };
  }
};

export const fetchStudentById = async (id: string): Promise<Student> => {
  const response = await getStudentById(id);
  return response as unknown as any;
};

export const suspendStudent = async (
  id: string,
  reason?: string
): Promise<ApiResponse<StudentWithUserInfo>> => {
  try {
    const response = await api.patch(`/students/${id}/suspend`, { reason });
    return response as unknown as any;
  } catch (error) {
    console.error("Error suspending student:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to suspend student",
    };
  }
};

/**
 * Download admission letter for a student
 * @param studentId - Student ID
 * @returns Blob URL for the admission letter PDF
 */
export const downloadAdmissionLetter = async (
  studentId: string
): Promise<string> => {
  try {
    const response = await api.get(`/admission-letters/${studentId}/download`, {
      responseType: "blob",
    });

    // Create a blob URL from the response
    const blob = new Blob([response as any], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    return url;
  } catch (error) {
    console.error("Error downloading admission letter:", error);
    throw error;
  }
};
